
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from '../../../services/WebsiteGroup/interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { UserBaseInfoInterface, UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import UserPicker from '@/components/user/UserPicker.vue';
import WebsitePicker from '@/components/website/WebsitePicker.vue';
import { PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';

@Component({
  components: {
    WebsitePicker,
    UserPicker,
  },
  mixins: [Themeable],
  inject: [
    'websiteGroupRepository',
    'websiteRepository',
    'userRepository',
    'planRepository',
    'serverRepository',
  ],
})
export default class AllWebsites extends Vue {
  public loading: boolean = true;

  public userSearchLoading = true;

  public usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [];

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public totalGroupsCount = 0;
  public ownerFilter: string | null = null;

  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private planRepository!: PlanRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'HB ID', value: 'hbId' },
    { text: 'Plan', value: 'plan' },
    { text: 'Main Website', value: 'mainWebsite' },
    { text: 'Server', value: 'server' },
    { text: 'Owner', value: 'owner' },
    { text: 'Website Count', value: 'websiteCount' },
  ];

  public groups: WebsiteGroupItem[] = [];
  public websites: Website[] = [];
  public users: UserBaseInfoInterface[] = [];
  public plansMap: { [key: number]: string } = {};

  public async created(): Promise<void> {
    await this.loadPlans();
    await this.loadUsersForSelect();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;

      await this.loadWebsiteGroups();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(`An issue ocurred while retrieving website groups. ${err}`);
    }
  }

  public async loadWebsiteGroups(clearPage?: boolean): Promise<void> {
    try {
      this.loading = true;
      if (clearPage) {
        this.options.page = 1;
      }


      const groupsData = await this.websiteGroupRepository.searchGroups(undefined, this.ownerFilter, this.options.page);

      if (!groupsData?.groups) {
        this.groups = [];
        this.totalGroupsCount = 0;
        this.loading = false;

        return;
      }

      this.groups = groupsData.groups;
      this.totalGroupsCount = groupsData.count;

      if (clearPage) {
        await this.loadWebsites();
        await this.loadUsers();
        await this.loadServers();
      }

      this.loading = false;
    } catch (err) {
      this.groups = [];
      this.totalGroupsCount = 0;
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  private async loadWebsites(): Promise<void> {
    const ids = this.groups.map((group) => group.mainWebsite);
    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  private async loadUsers(): Promise<void> {
    const ids = this.groups.map((group) => group.owner).filter((owner) => owner.length === 24);
    if (!ids.length) {
      return;
    }

    this.users = await this.userRepository.getUsersByIds(ids);
  }

  private async loadUsersForSelect(): Promise<void> {
    try {
      const usersResponse = await this.userRepository.getAllUsers(0);
      const usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [{ header: 'Fenix' }];
      usersForSelect.push(...usersResponse[0]);

      const hbOwners = await this.websiteGroupRepository.getHBOwners();
      usersForSelect.push({ divider: true }, { header: 'Hostbill' });

      usersForSelect.push(
        ...hbOwners.map((owner) => ({ id: owner, username: `HB ${owner}` } as unknown as UserInterface)),
      );

      this.usersForSelect = usersForSelect;

      this.userSearchLoading = false;
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }


  public async loadPlans(): Promise<void> {
    const plans = await this.planRepository.getAll();
    plans.forEach((plan) => {
      this.plansMap[plan.id] = plan.name;
    });
  }

  public async loadServers(): Promise<void> {
    const serversIds: string[] = [];
    this.websites.forEach((website) => {
      if (!serversIds.includes(website.originServer)) {
        serversIds.push(website.originServer);
      }
    });

    if (!serversIds.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(serversIds);
    const serversMap: { [key: string]: string } = {};
    servers.forEach((server) => {
      serversMap[server._id] = server.hostname;
    });


    this.websites.forEach((website) => {
      const groupIndex = this.groups.findIndex((g) => g.mainWebsite === website.id);
      if (groupIndex === -1) {
        return;
      }

      this.$set(this.groups[groupIndex], 'server', serversMap[website.originServer]);
    });
  }


  private getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  private getUserEmail(userId: string): string {
    const user = this.users.find((w) => w.id === userId);
    if (!user) {
      return '';
    }

    return user.email;
  }
}
